import moment from 'moment-timezone';
import pkg from '../../../package.json';
import { ThemeColorPresets } from '../../theme';

const { version } = pkg;
const LegoLocalStorageKey = '__maga_lego_ls_key__';
const CrewLocalStorageKey = '__maga_crew_ls_key__';
const APILocalStorageKey = '__maga_api_ls_key__';
const TimezoneLocalStorageKey = '__maga_tzone_ls_key__';
const ThemeLocalStorageKey = '__maga_themecolor_ls_key__';
const DarkModeLocalStorageKey = '__maga_darkmode_ls_key__';
const DrawerShrinkLocalStorageKey = '__maga_drawerShrink_ls_key__';

const isReleaseCandidate = /[0-9]+\.[0-9]+\.[0-9]+-rc\.[0-9]+/.test(version);

class Settings {
  constructor() {
    this.applyDefaultTimezoneToMomentObject();
  }

  setThemeColor(hex: string) {
    window.localStorage.setItem(ThemeLocalStorageKey, hex);
  }

  getThemeColor() {
    const defaultTheme = process.env.ENV === 'production' ? ThemeColorPresets[0].hex : ThemeColorPresets[2].hex;
    return window.localStorage.getItem(ThemeLocalStorageKey) || defaultTheme;
  }

  setDarkMode(dark: boolean) {
    window.localStorage.setItem(DarkModeLocalStorageKey, dark ? 'true' : 'false');
  }

  getDrawerShrink() {
    return window.localStorage.getItem(DrawerShrinkLocalStorageKey) === 'true' || false;
  }

  setDrawerShrink(shrink: boolean) {
    window.localStorage.setItem(DrawerShrinkLocalStorageKey, shrink ? 'true' : 'false');
  }

  getDarkMode() {
    return window.localStorage.getItem(DarkModeLocalStorageKey) === 'true' || false;
  }

  setLegoURI(url: string) {
    window.localStorage.setItem(LegoLocalStorageKey, url);
  }

  setLegoURIDefault() {
    window.localStorage.setItem(LegoLocalStorageKey, (isReleaseCandidate ? process.env.LEGO_BETA_URI : process.env.LEGO_DEFAULT_URI) as string);
  }

  getLegoURI() {
    if (process.env.ENV === 'production') {
      return process.env.LEGO_DEFAULT_URI;
    }

    return window.localStorage.getItem(LegoLocalStorageKey) || (isReleaseCandidate ? process.env.LEGO_BETA_URI : process.env.LEGO_DEFAULT_URI);
  }

  isLegoURIModified() {
    return this.getLegoURI() !== process.env.LEGO_DEFAULT_URI;
  }

  setCrewURI(url: string) {
    window.localStorage.setItem(CrewLocalStorageKey, url);
  }

  setCrewURIDefault() {
    window.localStorage.setItem(CrewLocalStorageKey, (isReleaseCandidate ? process.env.CREW_BETA_URI : process.env.CREW_DEFAULT_URI) as string);
  }

  getCrewURI() {
    if (process.env.ENV === 'production') {
      return process.env.CREW_DEFAULT_URI;
    }
    return window.localStorage.getItem(CrewLocalStorageKey) || (isReleaseCandidate ? process.env.CREW_BETA_URI : process.env.CREW_DEFAULT_URI);
  }

  isCrewURIModified() {
    return this.getCrewURI() !== process.env.CREW_DEFAULT_URI;
  }

  setApiURI(url: string) {
    let composedUrl = url.trim();

    if (composedUrl.substr(composedUrl.length - 1) === '/') {
      composedUrl = composedUrl.substr(0, composedUrl.length - 1);
    }

    window.localStorage.setItem(APILocalStorageKey, encodeURI(composedUrl));
  }

  setApiURIDefault() {
    this.setApiURI((isReleaseCandidate ? process.env.API_BETA_URI : process.env.API_DEFAULT_URI) as string);
  }

  getApiURI() {
    if (process.env.ENV === 'production') {
      return process.env.API_DEFAULT_URI;
    }

    return window.localStorage.getItem(APILocalStorageKey) || (isReleaseCandidate ? process.env.API_BETA_URI : process.env.API_DEFAULT_URI);
  }

  isApiURIModified() {
    return this.getApiURI() !== process.env.API_DEFAULT_URI;
  }

  setTimezone(url: string) {
    window.localStorage.setItem(TimezoneLocalStorageKey, url);
    this.applyDefaultTimezoneToMomentObject();
  }

  setTimezoneDefault() {
    this.setTimezone(this.getDefaultTimezoneName());
  }

  applyDefaultTimezoneToMomentObject() {
    const tz = this.getTimezone() || this.getDefaultTimezoneName();

    moment.tz.setDefault(tz);
  }

  getTimezone() {
    return window.localStorage.getItem(TimezoneLocalStorageKey) || this.getDefaultTimezoneName();
  }

  getTimezoneAbbr() {
    return moment().format('z');
  }

  getDefaultTimezoneName() {
    return 'America/New_York';
  }

  getTimezoneNameList() {
    const timezones = moment.tz.names();
    timezones.unshift(this.getDefaultTimezoneName());

    return timezones;
  }

  getVersion() {
    return version;
  }
}

export default new Settings();
